/*
 * =============================================================================
 * FILE HEADER — packages/ir/src/validate.ts
 * =============================================================================
 *
 * CHANGE LOG
 * ----------
 * | # | Date & Time (IST)      | Who               | Why             | What Changed               |
 * |---|------------------------|-------------------|-----------------|----------------------------|
 * | 1 | 2026-06-16 (Phase 1)   | Claude Sonnet 4.6 | IR Step 1       | validateIR + parseIR       |
 *
 * WHICH FILE CALLS / IMPORTS THIS FILE
 * --------------------------------------
 * - packages/ir/src/index.ts       (re-exported)
 * - packages/runner/src/index.ts   (calls validateIR before execution)
 * - packages/cli/src/index.ts      (validates loaded JSON before run)
 * - packages/sdk/src/index.ts      (calls validateIR in build() to catch SDK errors)
 *
 * FUNCTIONS / EXPORTS COVERED
 * ----------------------------
 * IRValidationError  — Error subclass carrying zod ZodError details
 * ValidationResult   — { ok: true; ir: TestIR } | { ok: false; error: IRValidationError }
 * validateIR(raw)    — safe validate; returns ValidationResult (never throws)
 * parseIR(raw)       — strict validate; returns TestIR or throws IRValidationError
 * parseIRFromJson(s) — parse a JSON string, then validate
 *
 * CALLER → CALLEE MAP
 * --------------------
 * validateIR → TestIRSchema.safeParse()
 * parseIR    → TestIRSchema.parse() (throws on failure)
 * parseIRFromJson → JSON.parse → parseIR
 *
 * IMPORTANT POINTS
 * -----------------
 * 1. validateIR never throws — it returns a discriminated union so callers can
 *    pattern-match on `result.ok` without a try/catch.
 * 2. parseIR is the "fast path" for the runner/CLI; if the IR was already
 *    validated at authoring time, parse failures are programming errors.
 * 3. The `unknown` input type is intentional — IR arrives as JSON.parse() output
 *    or from HTTP request bodies; we never trust the shape before parsing.
 * 4. IRValidationError.issues exposes the raw ZodIssue[] for structured error
 *    reporting (the CLI can pretty-print field paths and messages).
 *
 * ISSUES FACED
 * ------------
 * None
 *
 * =============================================================================
 */
import { TestIRSchema } from "./schemas/test-ir.js";
// ── Error type ───────────────────────────────────────────────────────────────
export class IRValidationError extends Error {
    issues;
    constructor(issues) {
        const summary = issues
            .slice(0, 3)
            .map((i) => `${i.path.join(".")}: ${i.message}`)
            .join("; ");
        super(`IR validation failed — ${summary}${issues.length > 3 ? ` (and ${issues.length - 3} more)` : ""}`);
        this.name = "IRValidationError";
        this.issues = issues;
    }
}
// ── Validate (safe, never throws) ────────────────────────────────────────────
/**
 * Validate raw JSON against the TestIR schema.
 * Returns { ok: true, ir } on success or { ok: false, error } on failure.
 */
export function validateIR(raw) {
    const result = TestIRSchema.safeParse(raw);
    if (result.success)
        return { ok: true, ir: result.data };
    return { ok: false, error: new IRValidationError(result.error.issues) };
}
// ── Parse (strict, throws on failure) ────────────────────────────────────────
/**
 * Validate raw JSON and return a TestIR, throwing IRValidationError on failure.
 * Use when the IR is expected to be valid (e.g. in the runner after CLI validation).
 */
export function parseIR(raw) {
    const result = TestIRSchema.safeParse(raw);
    if (result.success)
        return result.data;
    throw new IRValidationError(result.error.issues);
}
/**
 * Parse a JSON string and validate as TestIR.
 * Throws SyntaxError on invalid JSON, IRValidationError on schema violations.
 */
export function parseIRFromJson(json) {
    return parseIR(JSON.parse(json));
}
//# sourceMappingURL=validate.js.map